import {
  type ExprAndNode,
  type ExprNode,
  type ExprOrNode,
  type ExprRangeNode,
  type ExprTextNode,
  type ExprValueNode,
  PositionCategoryKind,
  PositionRangeKind,
  PositionTextKind,
  type UserCategoryKind,
  type UserExprTextKind,
  type UserNode,
  type UserRangeKind,
} from '@revelio/data-access';

import type { TreeFilter } from '../../../filters/types/filters';
import { getActiveSelections } from '../../../filters/utils/primaryFilterUtils';
import type { TalentDiscoveryV3Tags } from './types';

/**
 * Build a UserNode that wraps an ExprNode in a user_value node
 */
const buildUserValueNode = (exprNode: ExprNode): UserNode => ({
  user_value: {
    child: exprNode,
  },
});

/**
 * Build a user_and node for combining multiple UserNodes (AND logic across different positions)
 */
const buildUserAndNode = (children: UserNode[]): UserNode => ({
  user_and: {
    children,
  },
});

/**
 * Build a user_not node for negating a UserNode (NOT logic)
 */
const buildUserNotNode = (child: UserNode): UserNode => ({
  user_not: {
    child,
  },
});

/**
 * Build a UserNode from an ExprNode, optionally wrapped in user_not.
 * This is the standard way to create filter nodes - it handles the NOT logic
 * at the UserNode level (user_not > user_value) rather than ExprNode level.
 *
 * @param exprNode - The expression node to wrap
 * @param isNot - Whether to wrap in user_not (negation)
 * @returns UserNode with optional NOT wrapper
 */
export const wrapExprNode = (exprNode: ExprNode, isNot?: boolean): UserNode => {
  const userValueNode = buildUserValueNode(exprNode);
  return isNot ? buildUserNotNode(userValueNode) : userValueNode;
};

/**
 * Build a val node for a single value filter (returns ExprNode)
 */
export const buildValNode = (
  kind: TalentDiscoveryV3Tags,
  val: number,
  curr: boolean | undefined = true
): { val: ExprValueNode } => {
  // Determine if this is a position or user attribute based on the kind
  const isPositionAttribute = Object.values(PositionCategoryKind).includes(
    kind as PositionCategoryKind
  );

  if (isPositionAttribute) {
    return {
      val: {
        pos_attribute: {
          kind: kind as PositionCategoryKind,
          id: val,
          curr: curr ?? true,
        },
      },
    };
  } else {
    // Must be a user attribute
    return {
      val: {
        user_attribute: {
          kind: kind as UserCategoryKind,
          id: val,
        },
      },
    };
  }
};

/**
 * Build an or node for multiple values (OR logic) - returns ExprNode
 */
export const buildOrNode = (children: ExprNode[]): { or: ExprOrNode } => ({
  or: {
    children,
  },
});

/**
 * Build an and node for combining multiple filters (AND logic) - returns ExprNode
 */
const buildAndNode = (children: ExprNode[]): { and: ExprAndNode } => ({
  and: {
    children,
  },
});

/**
 * Flatten nested user_and nodes in UserNode. If a node is a user_and node, extract its children.
 */
const flattenUserAndNodes = (node: UserNode): UserNode[] => {
  if (node.user_and?.children) {
    return node.user_and.children.flatMap(flattenUserAndNodes);
  }
  return [node];
};

/**
 * Check if an ExprNode contains position attributes with different curr values in an AND context.
 * This indicates we need to split it into separate user_value nodes.
 * Handles NOT at the ExprNode level by checking the inner node.
 */
const hasPositionAttributesWithDifferentCurrInAnd = (
  node: ExprNode
): boolean => {
  // If there's a NOT wrapper, check the inner node
  const nodeToCheck = node.not?.child ?? node;

  if (!nodeToCheck.and?.children) {
    return false;
  }

  const currValues = new Set<boolean | undefined>();
  for (const child of nodeToCheck.and.children) {
    // Check if this child has a position attribute
    if (child.val?.pos_attribute) {
      currValues.add(child.val.pos_attribute.curr);
    } else if (child.rng?.pos_attribute) {
      currValues.add(child.rng.pos_attribute.curr);
    } else if (child.txt?.pos_attribute) {
      currValues.add(child.txt.pos_attribute.curr);
    }
  }
  // If we have both true and false curr values, we need to split
  return currValues.has(true) && currValues.has(false);
};

/**
 * Split an ExprNode with position attributes having different curr values into separate UserNodes.
 * Each position attribute group with the same curr value gets its own user_value node.
 * This is used when ANDing position attributes with different curr values.
 * A single position cannot be both curr: true and curr: false at the same time.
 * Handles NOT at the ExprNode level by extracting the inner node, splitting it, and applying NOT to each split.
 */
const splitPositionAttributesByCurr = (node: ExprNode): UserNode[] => {
  // Handle NOT at ExprNode level: extract inner node, split it, then apply NOT to each split
  if (node.not?.child) {
    const innerNode = node.not.child;
    const splitNodes = splitPositionAttributesByCurr(innerNode);
    // Apply NOT at UserNode level to each split node
    return splitNodes.map((userNode) => buildUserNotNode(userNode));
  }

  if (!node.and?.children) {
    // If it's not an AND node, just wrap it
    return [buildUserValueNode(node)];
  }

  // Group children by curr value for position attributes
  const trueCurrChildren: ExprNode[] = [];
  const falseCurrChildren: ExprNode[] = [];
  const otherChildren: ExprNode[] = [];

  for (const child of node.and.children) {
    let hasPositionAttr = false;
    let curr: boolean | undefined;

    if (child.val?.pos_attribute) {
      hasPositionAttr = true;
      curr = child.val.pos_attribute.curr;
    } else if (child.rng?.pos_attribute) {
      hasPositionAttr = true;
      curr = child.rng.pos_attribute.curr;
    } else if (child.txt?.pos_attribute) {
      hasPositionAttr = true;
      curr = child.txt.pos_attribute.curr;
    }

    if (hasPositionAttr) {
      if (curr === true) {
        trueCurrChildren.push(child);
      } else if (curr === false) {
        falseCurrChildren.push(child);
      } else {
        otherChildren.push(child);
      }
    } else {
      // Non-position attributes can go with any group
      otherChildren.push(child);
    }
  }

  const userNodes: UserNode[] = [];

  // Create user_value nodes for each curr group
  if (trueCurrChildren.length > 0) {
    const combinedChildren = [...trueCurrChildren, ...otherChildren];
    if (combinedChildren.length === 1) {
      const firstChild = combinedChildren[0];
      if (firstChild) {
        userNodes.push(buildUserValueNode(firstChild));
      }
    } else {
      userNodes.push(buildUserValueNode(buildAndNode(combinedChildren)));
    }
  }

  if (falseCurrChildren.length > 0) {
    // Only add otherChildren if we didn't already add them
    const combinedChildren =
      trueCurrChildren.length > 0
        ? falseCurrChildren
        : [...falseCurrChildren, ...otherChildren];
    if (combinedChildren.length === 1) {
      const firstChild = combinedChildren[0];
      if (firstChild) {
        userNodes.push(buildUserValueNode(firstChild));
      }
    } else {
      userNodes.push(buildUserValueNode(buildAndNode(combinedChildren)));
    }
  }

  // If no position attributes, just wrap the whole thing
  if (userNodes.length === 0) {
    return [buildUserValueNode(node)];
  }

  return userNodes;
};

/**
 * Check if a UserNode contains an ExprNode with position attributes that have different curr values in an AND.
 * If so, split it into separate UserNodes.
 */
const splitUserNodeIfNeeded = (userNode: UserNode): UserNode[] => {
  // Only check user_value nodes (not user_and, user_or, user_not)
  if (!userNode.user_value?.child) {
    return [userNode];
  }

  const exprNode = userNode.user_value.child;

  // Check if this ExprNode has an AND with position attributes with different curr values
  if (hasPositionAttributesWithDifferentCurrInAnd(exprNode)) {
    // Split the ExprNode (handles NOT at ExprNode level if present)
    const splitNodes = splitPositionAttributesByCurr(exprNode);
    // Note: If there was a user_not wrapper (rare case), we'd need to preserve it,
    // but with NOT at ExprNode level, this is handled in splitPositionAttributesByCurr
    if (userNode.user_not) {
      // If the original had user_not at UserNode level, apply it to each split node
      return splitNodes.map((node) => buildUserNotNode(node));
    }
    return splitNodes;
  }

  return [userNode];
};

/**
 * Compose multiple filter nodes into a single UserNode expression.
 * Wraps ExprNodes in user_value nodes and uses user_and to combine them.
 * Handles position attributes with different curr values by splitting them appropriately.
 * A single position cannot be both curr: true and curr: false at the same time.
 */
export const composeFilterNodes = (
  nodes: Array<UserNode | UserNode[] | null>
): UserNode => {
  // Flatten arrays and filter out nulls, then flatten nested user_and nodes
  // Also split any UserNodes that have position attributes with different curr values in AND contexts
  const flattenedNodes = nodes
    .flatMap((node) => {
      if (node === null) return [];
      if (Array.isArray(node)) return node;
      return [node];
    })
    .flatMap(flattenUserAndNodes)
    .flatMap(splitUserNodeIfNeeded);

  if (flattenedNodes.length === 0) {
    // Return empty user_and node
    return buildUserAndNode([]);
  }

  if (flattenedNodes.length === 1) {
    const [singleNode] = flattenedNodes;
    return singleNode ?? buildUserAndNode([]);
  }

  return buildUserAndNode(flattenedNodes);
};

/**
 * Convert an array of IDs to an ExprNode expression.
 * - Empty array: returns null
 * - Single ID: returns a val node
 * - Multiple IDs: returns an or node with val children
 */
export const idsToNode = (
  ids: number[],
  kind: TalentDiscoveryV3Tags,
  curr: boolean | undefined = true
): ExprNode | null => {
  if (ids.length === 0) return null;
  const firstId = ids[0];
  if (ids.length === 1 && firstId !== undefined) {
    return buildValNode(kind, firstId, curr);
  }

  return buildOrNode(ids.map((id) => buildValNode(kind, id, curr)));
};

/**
 * Extract numeric ID from selection.id string.
 * Handles IDs like "role_k7_123" by extracting the last segment after the final underscore.
 */
const extractNumericIdFromSelection = (selectionId: string): number | null => {
  const parts = selectionId.split('_');
  const lastPart = parts[parts.length - 1];
  if (!lastPart) return null;
  const numericId = parseInt(lastPart, 10);
  return Number.isFinite(numericId) ? numericId : null;
};

/**
 * Generic function to build tree filter nodes from a TreeFilter.
 * Groups selections by levelId, builds OR nodes for each levelId group,
 * and combines different levelId groups with OR logic.
 *
 * Example: Selecting "London" (city) and "Asia" (region) should return
 * people in London OR Asia, not people in both (which would be impossible).
 *
 * @param filter - The TreeFilter to process
 * @param levelIdToTagMap - Mapping from levelId strings to TalentDiscoveryV3Tags
 * @param isCurrent - Optional isCurrent flag (defaults to filter.isCurrent ?? true)
 * @returns ExprNode expression or null if no valid selections
 */
export const buildTreeFilterNodes = (
  filter: TreeFilter,
  levelIdToTagMap: Record<string, TalentDiscoveryV3Tags>,
  isCurrent?: boolean
): ExprNode | null => {
  // Use primarySelections if available, filtering by isActive, else selections
  const selections = filter.primarySelections
    ? getActiveSelections(filter.primarySelections)
    : filter.selections;
  if (!selections || selections.length === 0) return null;

  // Determine isCurrent value
  const curr = isCurrent ?? filter.isCurrent ?? true;

  // Group selections by levelId and extract numeric IDs
  const groupedByLevelId: Record<string, number[]> = {};

  for (const selection of selections) {
    const tag = levelIdToTagMap[selection.levelId];
    if (!tag) continue; // Skip unknown levelIds

    const numericId = extractNumericIdFromSelection(selection.id);
    if (numericId === null) continue; // Skip invalid IDs

    const levelId = selection.levelId;
    if (!groupedByLevelId[levelId]) {
      groupedByLevelId[levelId] = [];
    }
    const ids = groupedByLevelId[levelId];
    if (ids) {
      ids.push(numericId);
    }
  }

  // Build OR nodes for each levelId group
  const levelNodes = Object.entries(groupedByLevelId)
    .map(([levelId, ids]) => {
      const tag = levelIdToTagMap[levelId];
      if (!tag || ids.length === 0) return null;
      return idsToNode(ids, tag, curr);
    })
    .filter((node): node is ExprNode => node !== null);

  if (levelNodes.length === 0) return null;
  if (levelNodes.length === 1) {
    const [singleNode] = levelNodes;
    return singleNode ?? null;
  }

  // Combine different levelIds with OR logic
  // e.g., (London OR Paris) OR (Asia OR Europe) = London OR Paris OR Asia OR Europe
  return buildOrNode(levelNodes);
};

/**
 * Build a range node for range filters (e.g., graduation year, pay, years of experience)
 */
export const buildRangeNode = (
  kind: TalentDiscoveryV3Tags,
  start: number,
  end: number,
  curr = true
): { rng: ExprRangeNode } => {
  // Determine if this is a position or user attribute based on the kind
  const isPositionAttribute = Object.values(PositionRangeKind).includes(
    kind as PositionRangeKind
  );

  if (isPositionAttribute) {
    return {
      rng: {
        pos_attribute: {
          kind: kind as PositionRangeKind,
          start,
          end,
          curr,
        },
      },
    };
  } else {
    // Must be a user attribute
    return {
      rng: {
        user_attribute: {
          kind: kind as UserRangeKind,
          start,
          end,
        },
      },
    };
  }
};

/**
 * Build a text node for free text filters (e.g., keywords, name, titles)
 */
export const buildTxtNode = (
  kind: TalentDiscoveryV3Tags,
  text: string,
  curr = true
): { txt: ExprTextNode } => {
  // Determine if this is a position or user attribute based on the kind
  const isPositionAttribute = Object.values(PositionTextKind).includes(
    kind as PositionTextKind
  );

  if (isPositionAttribute) {
    return {
      txt: {
        pos_attribute: {
          kind: kind as PositionTextKind,
          text,
          curr,
        },
      },
    };
  } else {
    // Must be a user attribute
    return {
      txt: {
        user_attribute: {
          kind: kind as UserExprTextKind,
          text,
        },
      },
    };
  }
};
